import { compareDisplayNames } from '@utils/string.util';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCard } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { DeviceInfoService } from '@services/api/device-info.service';
import { LoadingService } from '@services/loading.service';
import {
  DeviceInfoListTableComponent,
  DeviceViewModel,
} from './device-info-list-table/device-info-list-table.component';
import { DeviceInfoMapContainerComponent } from './device-info-map-container/device-info-map-container.component';
import { ExportService } from '@services/export.service';
import { MatDialog } from '@angular/material/dialog';

/** タブ */
export type DisplayTypeViewModel = 'list' | 'map';

@Component({
  selector: 'app-device-info-list',
  standalone: true,
  imports: [
    FormsModule,
    MatCard,
    MatIconModule,
    MatButtonToggleModule,
    MatButtonModule,
    DeviceInfoListTableComponent,
    DeviceInfoMapContainerComponent,
  ],
  templateUrl: './device-info-list.component.html',
  styleUrl: './device-info-list.component.scss',
})
export class DeviceInfoListComponent implements OnInit {
  /** デバイス一覧 */
  public deviceList: DeviceViewModel[] = [];
  /** デバイス種類のリスト */
  public deviceTypes: string[] = [];
  /** タブ */
  public selectedDisplayType: DisplayTypeViewModel = 'list';

  constructor(
    private deviceInfoService: DeviceInfoService,
    private loadingService: LoadingService,
    private exportService: ExportService,
    private dialog: MatDialog,
  ) {}

  public ngOnInit(): void {
    this._initDeviceList();
  }

  public _initDeviceList(): void {
    this.deviceInfoService.findDeviceList().subscribe({
      next: (devices) => {
        this.deviceList = devices.map((device) => {
          return {
            /** デバイス名 */
            deviceName: device.deviceName || device.deviceId,
            /** シリアルナンバー(デバイスID) */
            serialNo: device.deviceId,
            /** デバイス種類 */
            deviceType: device.deviceTypeDisplayName,
            /** 最終通信日時 */
            lastCommunicationAt: device.lastCommunicationAt,
            /** ステータス */
            status: device.validContractState ? device.status : '契約終了',
            /** バッテリー */
            battery: device.battery,
            /** 設定グループ */
            settingGroupName: device.deviceSettingGroupName || '',
            /** タグ */
            tag: device.tagNames.sort((first, second) =>
              first > second ? 1 : -1,
            ),
            tagList: [
              ...(device.tagList || device.tagNames.map((tagName) => ({ tagId: tagName, tagName }))),
            ].sort((first, second) => first.tagName.localeCompare(second.tagName)),
            /** 契約終了日 */
            contractExpiredAt: device.contractExpiredAt
              ? new Date(device.contractExpiredAt)
              : null,
            validContractState: device.validContractState,
            /** フライトモードのON/OFF */
            fmdOn: device.fmdOn,
            /** フライトモードの有効/無効 */
            fmdEnabled: device.fmdEnabled,
            /** 位置情報 */
            location: device.location
              ? {
                  longitude: device.location.longitude,
                  latitude: device.location.latitude,
                  eventAt: device.location.eventAt,
                  address: device.location.address,
                }
              : undefined,
          };
        });
        this.sortDeviceInfoList(this.deviceList);
        // デバイス種類のリストを取得
        const displayNames = devices.map((device) => device.deviceTypeDisplayName);
        this.deviceTypes = Array.from(new Set(displayNames))
          .filter(Boolean)
          .sort(compareDisplayNames);
      },
    });
  }

  /**
   * 第一ソートキーを最終更新日時/第二ソートキーをデバイス名としてソートする
   * デバイス名は上から下に昇順で表示。最終更新日時は上から下に降順で表示。
   */
  private sortDeviceInfoList(deviceList: DeviceViewModel[]) {
    deviceList.sort((a, b) => {
      if (a.lastCommunicationAt === null && b.lastCommunicationAt === null) {
        return a.deviceName.localeCompare(b.deviceName);
      } else if (
        a.lastCommunicationAt === null ||
        a.lastCommunicationAt === undefined
      ) {
        return 1;
      } else if (
        b.lastCommunicationAt === null ||
        b.lastCommunicationAt === undefined
      ) {
        return -1;
      } else {
        return (
          new Date(b.lastCommunicationAt).getTime() -
          new Date(a.lastCommunicationAt).getTime()
        );
      }
    });
  }
}
